<template>
  <v-container fluid class="aml-dash pa-5">

    <!-- ══ Page loading ══ -->
    <template v-if="pageLoading">
      <v-skeleton-loader type="heading" width="220" class="mb-5" />
      <v-skeleton-loader type="card" height="400" />
    </template>

    <template v-else>

      <!-- ══ Header ══ -->
      <div class="d-flex align-center justify-space-between mb-5">
        <div class="d-flex align-center">
          <button class="aml-back-btn mr-3" @click="$router.push({ name: 'watchlist-dashboard' })">
            <v-icon small>mdi-arrow-left</v-icon>
          </button>
          <div>
            <div class="aml-overline mb-1">Watchlist Screening</div>
            <h2 class="aml-title">Search Reports</h2>
          </div>
        </div>
        <v-btn depressed small class="aml-btn-ghost" :loading="loading" @click="loadReports">
          <v-icon small left>mdi-refresh</v-icon>Refresh
        </v-btn>
      </div>

      <!-- ══ Reports table ══ -->
      <div class="aml-card">
        <div class="aml-toolbar">
          <span style="font-size:13px;font-weight:500">All search sessions</span>
          <div class="d-flex align-center" style="gap:8px">
            <v-text-field
              v-model="filters.dateFrom"
              label="From" type="date" dense outlined
              hide-details style="max-width:160px"
            />
            <v-text-field
              v-model="filters.dateTo"
              label="To" type="date" dense outlined
              hide-details style="max-width:160px"
            />
            <v-btn depressed x-small color="primary" @click="applyFilters">Apply</v-btn>
            <v-btn depressed x-small class="aml-btn-ghost" @click="clearFilters">Clear</v-btn>
          </div>
        </div>

        <div class="aml-table-wrap">
          <table class="aml-table">
            <colgroup>
              <col style="width:18%">
              <col style="width:12%">
              <col style="width:14%">
              <col style="width:10%">
              <col style="width:10%">
              <col style="width:10%">
              <col style="width:14%">
              <col style="width:12%">
            </colgroup>
            <thead>
              <tr>
                <th>Session</th>
                <th>Type</th>
                <th>Initiated by</th>
                <th>Accounts</th>
                <th>Searches</th>
                <th>Possible hits</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="8"><v-skeleton-loader type="table-row-divider@5" /></td>
              </tr>
              <tr v-else-if="!reports.length">
                <td colspan="8" class="aml-empty-row">No search reports found</td>
              </tr>
              <tr
                v-for="r in reports"
                :key="r.sessionId"
                class="aml-table-row"
                @click="viewReport(r.sessionId)"
              >
                <td>
                  <div class="aml-ref-link" style="font-size:11px;font-family:monospace">{{ r.sessionId }}</div>
                </td>
                <td>
                  <span class="aml-type-badge">{{ r.triggerType }}</span>
                </td>
                <td class="aml-muted">{{ r.initiatedBy ? assigneeName(r.initiatedBy) : 'System' }}</td>
                <td class="aml-muted">{{ r.totalAccounts }}</td>
                <td class="aml-muted">{{ r.totalSearches }}</td>
                <td>
                  <span
                    class="aml-pill"
                    :style="r.possibleMatches > 0 ? 'background:#FCEBEB;color:#A32D2D' : 'background:#EAF3DE;color:#3B6D11'"
                  >
                    {{ r.possibleMatches }}
                  </span>
                </td>
                <td class="aml-muted">{{ fmtDate(r.dateCreated) }}</td>
                <td @click.stop>
                  <div class="d-flex align-center justify-end" style="gap:4px">
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <button class="aml-icon-btn" v-on="on" :disabled="detailLoading" @click="viewReport(r.sessionId)">
                          <v-icon x-small color="#185FA5">mdi-eye</v-icon>
                        </button>
                      </template>
                      <span>View report</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <button class="aml-icon-btn" v-on="on" :disabled="detailLoading" @click="printReport(r.sessionId)">
                          <v-icon x-small color="#534AB7">mdi-printer-outline</v-icon>
                        </button>
                      </template>
                      <span>Print report</span>
                    </v-tooltip>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination footer -->
        <div class="aml-table-footer">
          <span class="aml-muted">{{ total }} report{{ total !== 1 ? 's' : '' }}</span>
          <div class="d-flex align-center" style="gap:6px">
            <v-btn x-small text :disabled="page <= 1" @click="prevPage">
              <v-icon x-small>mdi-chevron-left</v-icon>Prev
            </v-btn>
            <span class="aml-muted" style="font-size:11px">Page {{ page }}</span>
            <v-btn x-small text :disabled="reports.length < limit" @click="nextPage">
              Next<v-icon x-small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

    </template>

    <WatchlistReportPrint
      v-if="reportDetail"
      ref="watchlistReport"
      v-model="detailDialog"
      :report="reportDetail"
    />

  </v-container>
</template>

<script>
import { mapState }          from 'vuex';
import PageMixin             from '@/mixins/page.mixin';
import FormatMixin           from '@/mixins/format.mixin';
import AmlMixin              from '@/mixins/aml.mixin';
import { WatchlistService }  from '@/modules/amlpro/services/watchlist.service';
import WatchlistReportPrint  from './WatchlistReportPrint.vue';

const svc = new WatchlistService();

export default {
  name: 'WatchlistSearchReports',
  mixins: [PageMixin, FormatMixin, AmlMixin],
  components: { WatchlistReportPrint },

  data() {
    return {
      pageLoading:   true,
      loading:       false,
      reports:       [],
      total:         0,
      page:          1,
      limit:         20,
      filters:       { dateFrom: null, dateTo: null },

      detailDialog:  false,
      detailLoading: false,
      reportDetail:  null,
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),
  },

  async mounted() {
    await this.loadReports();
    this.pageLoading = false;
  },

  methods: {
    async loadReports() {
      this.loading = true;
      try {
        const params = { page: this.page, limit: this.limit };
        if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom;
        if (this.filters.dateTo)   params.dateTo   = this.filters.dateTo;
        const res      = await svc.fetchSearchReports(params);
        const data     = res.data ?? res;
        this.reports   = data.data ?? data;
        this.total     = data.total ?? this.reports.length;
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load reports' });
      } finally {
        this.loading = false;
      }
    },

    // Loads the detail (if needed) then OPENS the on-screen report viewer.
    async viewReport(sessionId) {
      await this.ensureReportDetail(sessionId);
      if (this.reportDetail) this.detailDialog = true;
    },

    // Loads the detail (if needed) then goes STRAIGHT to the browser print
    // dialog via the report component's own printReport() method — does
    // NOT open the on-screen viewer first, and does NOT use window.print()
    // (that only worked with the old hidden-DOM @media-print approach;
    // WatchlistReportPrint now builds its own print HTML and opens its
    // own print iframe internally).
    async printReport(sessionId) {
      await this.ensureReportDetail(sessionId);
      if (!this.reportDetail) return;
      // WatchlistReportPrint is v-if="reportDetail", so if it just mounted
      // this tick, wait for the ref to exist before calling into it.
      await this.$nextTick();
      this.$refs.watchlistReport?.printReport();
    },

    async ensureReportDetail(sessionId) {
      if (this.reportDetail && this.reportDetail.sessionId === sessionId) return;
      this.detailLoading = true;
      this.reportDetail  = null;
      try {
        const res         = await svc.fetchSearchReport(sessionId);
        this.reportDetail = res.data ?? res;
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load report detail' });
      } finally {
        this.detailLoading = false;
      }
    },

    applyFilters() { this.page = 1; this.loadReports(); },
    clearFilters()  { this.filters = { dateFrom: null, dateTo: null }; this.page = 1; this.loadReports(); },
    prevPage()      { if (this.page > 1) { this.page--; this.loadReports(); } },
    nextPage()      { this.page++; this.loadReports(); },
  },
};
</script>

<style scoped>
.aml-dash         { background:transparent; }
.aml-overline     { font-size:11px; font-weight:600; letter-spacing:1px; text-transform:uppercase; color:#999; }
.aml-title        { font-size:22px; font-weight:500; letter-spacing:-0.3px; margin:0; }
.aml-back-btn     { width:34px; height:34px; border-radius:8px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.12s; flex-shrink:0; }
.aml-back-btn:hover { background:#f5f5f5; }
.aml-btn-ghost    { font-size:12px !important; border:0.5px solid #ddd !important; background:transparent !important; color:inherit !important; box-shadow:none !important; }
.aml-card         { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; }
.aml-toolbar      { padding:12px 16px; border-bottom:0.5px solid #e0e0e0; display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
.aml-table-wrap   { overflow-x:auto; }
.aml-table        { width:100%; border-collapse:collapse; font-size:12px; table-layout:fixed; }
.aml-table thead tr { border-bottom:0.5px solid #e0e0e0; }
.aml-table th     { padding:9px 14px; text-align:left; font-size:10px; font-weight:600; color:#aaa; letter-spacing:.5px; text-transform:uppercase; }
.aml-table-row    { border-bottom:0.5px solid #e0e0e0; cursor:pointer; transition:background 0.1s; }
.aml-table-row:hover { background:#EBF3FC; }
.aml-table td     { padding:10px 14px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.aml-empty-row    { padding:32px 20px; text-align:center; color:#aaa; font-size:13px; }
.aml-table-footer { padding:9px 16px; border-top:0.5px solid #e0e0e0; font-size:11px; color:#aaa; display:flex; align-items:center; justify-content:space-between; }
.aml-muted        { color:#777; }
.aml-ref-link     { color:#185FA5; font-weight:600; }
.aml-type-badge   { font-size:10px; font-weight:700; color:#666; background:#f5f5f5; padding:2px 6px; border-radius:4px; }
.aml-pill         { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:500; white-space:nowrap; }
.aml-icon-btn     { width:24px; height:24px; border-radius:6px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; padding:0; transition:background 0.1s; }
.aml-icon-btn:hover { background:#f0f0f0; }
.aml-icon-btn:disabled { opacity:0.5; cursor:not-allowed; }
</style>